/* ============================================================================
   The approved brief, made checkable — and the target/designed space audit.

   Once the client accepts the brief it becomes the measure of the design:
   every space requirement and every substantive section turns into a line the
   studio checks off as the design answers it. Space lines also record what the
   design PROVIDES, so the brief's targets and the drawn scheme can be read
   against each other — per space and in total — all the way through the design
   stages. Shared, the client sees the same audit in their portal.
   ========================================================================== */
import { useState } from "react";
import { Icon } from "./Icons";
import { Meter } from "./ui";
import { actions } from "../lib/store";
import { unitLabel } from "../lib/program";
import { fmtDate } from "../lib/format";
import type { Project, ChecklistItem } from "../lib/types";

import { auditRows, AUDIT_STATE_LABEL, type AuditRow } from "../lib/spaceAudit";

const STATE_COLOR: Record<AuditRow["state"], string> = {
  unmeasured: "var(--ink-3)", ok: "var(--sage, #7a9d54)", near: "var(--gold, #c9962e)", off: "var(--accent-ink, #c0504d)",
};
const STATE_LABEL = AUDIT_STATE_LABEL;

/** The target vs designed report table — identical for studio and client. */
export function SpaceAuditTable({ p }: { p: Project }) {
  const unit = unitLabel(p.brief.areaUnit ?? "sqft");
  const { rows, targetTotal, designedTotal, measured, measuredTargetTotal } = auditRows(p);
  if (!rows.length) return null;
  const totalDelta = measured > 0 && measuredTargetTotal > 0 ? ((designedTotal - measuredTargetTotal) / measuredTargetTotal) * 100 : null;
  return (
    <div style={{ overflowX: "auto", border: "1px solid var(--line)", borderRadius: 12 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
        <thead>
          <tr style={{ textAlign: "left" }}>
            {["Space", `Target (${unit})`, `Designed (${unit})`, "Δ", ""].map((h, i) => (
              <th key={i} style={{ padding: "9px 12px", borderBottom: "1px solid var(--line)", fontSize: 10, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--ink-3)" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} style={{ borderBottom: "1px solid var(--line)" }}>
              <td style={{ padding: "8px 12px", fontWeight: 500 }}>{r.name}{r.targetQty > 1 ? <span className="dim"> ×{r.targetQty}</span> : null}</td>
              <td className="mono" style={{ padding: "8px 12px" }}>{r.targetTotal.toLocaleString()}<span className="dim" style={{ fontSize: 10.5 }}> ({r.targetQty} × {r.targetArea})</span></td>
              <td className="mono" style={{ padding: "8px 12px" }}>
                {r.designedTotal != null
                  ? <>{r.designedTotal.toLocaleString()}{r.designedQty !== r.targetQty || true ? <span className="dim" style={{ fontSize: 10.5 }}> ({r.designedQty} × {r.designedArea})</span> : null}</>
                  : <span className="dim">—</span>}
              </td>
              <td className="mono" style={{ padding: "8px 12px", color: STATE_COLOR[r.state] }}>
                {r.deltaPct != null ? `${r.deltaPct > 0 ? "+" : ""}${r.deltaPct.toFixed(Math.abs(r.deltaPct) < 10 ? 1 : 0)}%` : "—"}
              </td>
              <td style={{ padding: "8px 12px" }}>
                <span className="row" style={{ gap: 6, alignItems: "center", fontSize: 10.5, color: STATE_COLOR[r.state] }}>
                  <span style={{ width: 8, height: 8, borderRadius: 8, background: STATE_COLOR[r.state], display: "inline-block" }} />
                  {STATE_LABEL[r.state]}
                </span>
              </td>
            </tr>
          ))}
          <tr>
            <td style={{ padding: "10px 12px", fontWeight: 700 }}>Total</td>
            <td className="mono" style={{ padding: "10px 12px", fontWeight: 700 }}>{targetTotal.toLocaleString()}</td>
            <td className="mono" style={{ padding: "10px 12px", fontWeight: 700 }}>{measured ? designedTotal.toLocaleString() : "—"}</td>
            <td className="mono" style={{ padding: "10px 12px", fontWeight: 700 }}>{totalDelta != null ? `${totalDelta > 0 ? "+" : ""}${totalDelta.toFixed(1)}%` : "—"}</td>
            <td style={{ padding: "10px 12px", fontSize: 10.5 }} className="dim">{measured}/{rows.length} measured</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/* ---------------------------------------------------------- studio panel -- */
export function BriefChecklistPanel({ p, canEdit, by }: { p: Project; canEdit: boolean; by: string }) {
  const [showReport, setShowReport] = useState(true);
  const cl = p.briefChecklist;
  if (p.brief.status !== "approved") return null;

  if (!cl) {
    return canEdit ? (
      <div className="card pad reveal" style={{ marginBottom: 18 }}>
        <div className="row between wrap" style={{ gap: 10 }}>
          <div>
            <div className="kicker" style={{ marginBottom: 6 }}>The brief is approved</div>
            <div style={{ fontSize: 13.5, maxWidth: 520, lineHeight: 1.6 }}>
              Turn it into the design-stage checklist: every space and requirement becomes a line to check off as the design answers it, with a live target-vs-designed area report.
            </div>
          </div>
          <button className="btn accent" onClick={() => actions.generateBriefChecklist(p.id, by)}><Icon.check width={15} />Build the design checklist</button>
        </div>
      </div>
    ) : null;
  }

  const done = cl.items.filter((i) => i.done).length;
  const spaceItems = cl.items.filter((i) => i.kind === "space");
  const reqItems = cl.items.filter((i) => i.kind === "requirement");
  const unit = unitLabel(p.brief.areaUnit ?? "sqft");

  return (
    <div className="card pad reveal" style={{ marginBottom: 18 }}>
      <div className="row between wrap" style={{ gap: 10, marginBottom: 6 }}>
        <div className="row" style={{ gap: 10 }}>
          <Icon.check width={16} style={{ color: "var(--accent-ink)" }} />
          <span className="kicker">Design checklist — the brief, tracked</span>
        </div>
        <div className="row wrap" style={{ gap: 8 }}>
          {canEdit && <button className="btn ghost sm" onClick={() => actions.generateBriefChecklist(p.id, by)} title="Re-sync with the brief — nothing checked off is lost"><Icon.spark width={13} />Re-sync</button>}
          {canEdit && (
            <button className={"btn sm " + (cl.sharedWithClient ? "soft" : "ghost")} onClick={() => actions.shareBriefChecklist(p.id, !cl.sharedWithClient)}>
              <Icon.link width={13} />{cl.sharedWithClient ? "Shared with client" : "Share with client"}
            </button>
          )}
        </div>
      </div>
      <div className="row" style={{ gap: 12, alignItems: "center", marginBottom: 14 }}>
        <div style={{ flex: 1 }}><Meter value={cl.items.length ? (done / cl.items.length) * 100 : 0} /></div>
        <span className="mono dim" style={{ fontSize: 12 }}>{done}/{cl.items.length} answered</span>
      </div>

      {/* space lines — check off + record what the design provides */}
      {spaceItems.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div className="kicker" style={{ fontSize: 9.5, marginBottom: 8 }}>Spaces — target vs designed ({unit})</div>
          <div className="col" style={{ gap: 6 }}>
            {spaceItems.map((i) => {
              const space = (p.brief.spaces ?? []).find((s) => s.id === i.spaceId);
              return (
                <div key={i.id} className="row wrap panel" style={{ gap: 10, padding: "8px 12px", alignItems: "center", background: i.done ? "color-mix(in srgb, var(--sage, #7a9d54) 7%, var(--card))" : undefined }}>
                  <label className="row" style={{ gap: 8, flex: 2, minWidth: 180, cursor: canEdit ? "pointer" : "default", fontSize: 13 }}>
                    <input type="checkbox" disabled={!canEdit} checked={!!i.done} onChange={(e) => actions.toggleChecklistItem(p.id, i.id, e.target.checked, by)} />
                    <span style={{ fontWeight: 500 }}>{i.label}</span>
                    {space && <span className="dim mono" style={{ fontSize: 11 }}>target {space.qty} × {space.area}</span>}
                  </label>
                  <span className="row" style={{ gap: 6, alignItems: "center", fontSize: 11.5 }}>
                    <span className="dim">designed</span>
                    <input className="input mono" style={{ width: 52, padding: "4px 6px", fontSize: 12 }} inputMode="numeric" disabled={!canEdit}
                      value={i.designedQty ?? ""} placeholder={String(space?.qty ?? 1)}
                      onChange={(e) => actions.setChecklistItem(p.id, i.id, { designedQty: e.target.value === "" ? undefined : +e.target.value })} />
                    <span className="dim">×</span>
                    <input className="input mono" style={{ width: 72, padding: "4px 6px", fontSize: 12 }} inputMode="numeric" disabled={!canEdit}
                      value={i.designedArea ?? ""} placeholder="area"
                      onChange={(e) => actions.setChecklistItem(p.id, i.id, { designedArea: e.target.value === "" ? undefined : +e.target.value })} />
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* requirement lines from the brief's sections */}
      {reqItems.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div className="kicker" style={{ fontSize: 9.5, marginBottom: 8 }}>Requirements from the brief</div>
          <div className="col" style={{ gap: 6 }}>
            {reqItems.map((i) => (
              <div key={i.id} className="row wrap panel" style={{ gap: 10, padding: "8px 12px", alignItems: "center", background: i.done ? "color-mix(in srgb, var(--sage, #7a9d54) 7%, var(--card))" : undefined }}>
                <label className="row" style={{ gap: 8, flex: 1.4, minWidth: 160, cursor: canEdit ? "pointer" : "default", fontSize: 13 }}>
                  <input type="checkbox" disabled={!canEdit} checked={!!i.done} onChange={(e) => actions.toggleChecklistItem(p.id, i.id, e.target.checked, by)} />
                  <span style={{ fontWeight: 500 }}>{i.label}</span>
                  {i.done && i.doneOn && <span className="dim" style={{ fontSize: 10.5 }}>{i.doneBy} · {fmtDate(i.doneOn)}</span>}
                </label>
                <input className="input" style={{ flex: 2, minWidth: 180, padding: "4px 8px", fontSize: 12 }} disabled={!canEdit}
                  value={i.note ?? ""} placeholder="How the design answers it…"
                  onChange={(e) => actions.setChecklistItem(p.id, i.id, { note: e.target.value || undefined })} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* the report */}
      <div className="row between wrap" style={{ gap: 8, marginBottom: 8 }}>
        <span className="kicker" style={{ fontSize: 9.5 }}>Space report — target vs designed</span>
        <button className="btn ghost sm" onClick={() => setShowReport((v) => !v)}>{showReport ? "Hide" : "Show"}</button>
      </div>
      {showReport && <SpaceAuditTable p={p} />}
    </div>
  );
}

/* ---------------------------------------------------------- client view -- */
/** What the client sees in their portal once the studio shares the checklist:
 *  read-only progress against their own brief, and the same space report. */
export function ClientBriefTracker({ p }: { p: Project }) {
  const cl = p.briefChecklist;
  if (!cl?.sharedWithClient) return null;
  const done = cl.items.filter((i) => i.done).length;
  return (
    <div className="card pad reveal" style={{ marginBottom: 18 }}>
      <div className="kicker" style={{ marginBottom: 8 }}>Your brief, tracked against the design</div>
      <p className="muted" style={{ fontSize: 13.5, marginBottom: 12, maxWidth: 620 }}>
        Every requirement and target space from your approved brief, checked off as the design answers it — and how the designed areas compare to what you asked for.
      </p>
      <div className="row" style={{ gap: 12, alignItems: "center", marginBottom: 14 }}>
        <div style={{ flex: 1 }}><Meter value={cl.items.length ? (done / cl.items.length) * 100 : 0} /></div>
        <span className="mono dim" style={{ fontSize: 12 }}>{done}/{cl.items.length} answered</span>
      </div>
      <div className="col" style={{ gap: 4, marginBottom: 16 }}>
        {cl.items.map((i) => (
          <div key={i.id} className="row" style={{ gap: 8, fontSize: 13, padding: "4px 2px", alignItems: "baseline" }}>
            <span style={{ color: i.done ? "var(--sage, #7a9d54)" : "var(--ink-3)", flexShrink: 0 }}>{i.done ? "✓" : "○"}</span>
            <span style={{ fontWeight: 500 }}>{i.label}</span>
            {i.note && <span className="dim" style={{ fontSize: 12 }}>— {i.note}</span>}
          </div>
        ))}
      </div>
      <div className="kicker" style={{ fontSize: 9.5, marginBottom: 8 }}>Space report — target vs designed</div>
      <SpaceAuditTable p={p} />
    </div>
  );
}
